import { map } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import Button from 'components/Button';
import { Container, Grid, Cell } from 'components/Grid';
import { SecondaryHeading } from 'components/Headings';
import MediaGallery from 'components/MediaGallery';
import RichText from 'components/RichText';
import Theme from 'styles/theme';
import TransitionObserver from 'utils/TransitionObserver';

class ShowcaseRowsMobile extends PureComponent {
  constructor(props) {
    super(props);
    this.rowRefs = map(props.rows, () => React.createRef());
    this.state = { activeMediaGalleryIndex: 0 };
    this.transitionObserver = new TransitionObserver(this.rowRefs, props.handleRowTransition);
  }

  componentDidMount() {
    this.transitionObserver.observe();
  }

  componentWillUnmount() {
    this.transitionObserver.unobserve();
  }

  setActiveMediaGalleryIndex(index) {
    this.setState({ activeMediaGalleryIndex: index });
  }

  render() {
    const { rows } = this.props;

    return (
      <div className="ShowcaseRowsMobile">
        {map(rows, (content, _index) => {
          return (
            <div
              className="ShowcaseRowsMobile__row"
              ref={this.rowRefs[_index]}
              id={`row-${_index}`}
              key={`row-${_index}`}
            >
              <div className="ShowcaseRowsMobile__content">
                <Container>
                  <div className="ShowcaseRowsMobile__content-inner">
                    <SecondaryHeading>{content.heading}</SecondaryHeading>
                    <RichText content={content.subtext} />
                    <Grid className="ShowcaseRowsMobile__content-grid-cell">
                      {map(content.ctaLinks, ({ text, url, openInNewWindow }, index) => (
                        <Cell smallUp={{ columns: 12 }} key={index}>
                          <div className="ShowcaseRowsMobile__content-link-container">
                            <Button linkTo={url} target={openInNewWindow && '_blank'} isFullWidth>
                              {text}
                            </Button>
                          </div>
                        </Cell>
                      ))}
                    </Grid>
                  </div>
                </Container>
              </div>

              <div className="ShowcaseRowsMobile__media-gallery-container">
                <MediaGallery
                  media={content.mobileImages || [content.mobileImage]}
                  onIndexChange={index => this.setActiveMediaGalleryIndex(index)}
                  activeIndex={this.state.activeMediaGalleryIndex}
                />
              </div>
            </div>
          );
        })}

        <style jsx>
          {`
            .ShowcaseRowsMobile {
              position: relative;
              overflow: hidden;
            }

            .ShowcaseRowsMobile.ShowcaseRowsMobile--hasBackgroundColor {
              background-color: ${Theme.get('colors-grey')};
            }

            .ShowcaseRowsMobile > :nth-child(even) {
              background-color: ${Theme.get('colors-grey')};
            }

            .ShowcaseRowsMobile__content {
              display: flex;
              position: relative;
              justify-content: center;
              height: 100%;
              z-index: 3;
            }

            .ShowcaseRowsMobile__content-inner {
              padding: ${Theme.get('spacing') * 6}px 0;
            }

            .ShowcaseRowsMobile__content-link-container {
              margin-bottom: ${Theme.get('spacing') * 4}px;
            }
          `}
        </style>

        <style jsx global>
          {`
            .ShowcaseRowsMobile .ShowcaseRowsMobile__content .UnorderedList {
              margin-bottom: ${Theme.get('spacing') * 6}px;
            }

            .ShowcaseRowsMobile__content .UnorderedList .Paragraph {
              margin: 0;
            }

            .ShowcaseRowsMobile__content-inner .typography--secondary-heading {
              margin-bottom: ${Theme.get('spacing') * 2}px;
            }

            .ShowcaseRowsMobile__row {
              display: flex;
              flex-direction: column-reverse;
            }
          `}
        </style>
      </div>
    );
  }
}

ShowcaseRowsMobile.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string.isRequired,
      subtext: PropTypes.shape({}).isRequired,
      ctaLinks: PropTypes.arrayOf(
        PropTypes.shape({
          _contentType: PropTypes.string.isRequired,
          internalName: PropTypes.string.isRequired,
          text: PropTypes.string.isRequired,
          url: PropTypes.string.isRequired,
          openInNewWindow: PropTypes.bool.isRequired,
        })
      ),
      desktopImages: PropTypes.arrayOf(
        PropTypes.shape({
          file: PropTypes.shape({
            cloudinaryPublicId: PropTypes.string,
            details: PropTypes.shape({
              image: PropTypes.shape({
                height: PropTypes.number,
                width: PropTypes.number,
              }),
            }),
          }),
        })
      ),
      desktopImage: PropTypes.shape({
        file: PropTypes.shape({
          cloudinaryPublicId: PropTypes.string,
          details: PropTypes.shape({
            image: PropTypes.shape({
              height: PropTypes.number,
              width: PropTypes.number,
            }),
          }),
        }),
      }),
      mobileImages: PropTypes.arrayOf(
        PropTypes.shape({
          file: PropTypes.shape({
            cloudinaryPublicId: PropTypes.string,
            details: PropTypes.shape({
              image: PropTypes.shape({
                height: PropTypes.number,
                width: PropTypes.number,
              }),
            }),
          }),
        })
      ),
      mobileImage: PropTypes.shape({
        file: PropTypes.shape({
          cloudinaryPublicId: PropTypes.string,
          details: PropTypes.shape({
            image: PropTypes.shape({
              height: PropTypes.number,
              width: PropTypes.number,
            }),
          }),
        }),
      }),
    })
  ).isRequired,
  handleRowTransition: PropTypes.func.isRequired,
};

export default ShowcaseRowsMobile;
